import httpClient from './httpClient';
import { clearAuthData } from '../utils/auth';

class AuthService {
  // Admin login
  async adminLogin(email, password) {
    try {
      const payload = { email, password };
      
      const response = await httpClient.post('/api/admin/login', payload, { suppressAuthRedirect: true });

      if (response.success) {
        // Store the auth token
        httpClient.setAuthToken(response.data.token);
        
        // Store user data if needed
        localStorage.setItem('user_data', JSON.stringify(response.data.user));
        
        return {
          success: true,
          data: response.data,
          message: response.data.message
        };
      } else {
        // Handle different error statuses
        switch (response.status) {
          case 401:
            return {
              success: false,
              data: response.data,
              message: 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
              errorType: 'authentication',
              errorMessages: ['البريد الإلكتروني أو كلمة المرور غير صحيحة']
            };
          case 400:
            return {
              success: false,
              data: response.data,
              message: 'خطأ في تسجيل الدخول',
              errorType: 'authentication',
              errorMessages: [response.data?.message || 'خطأ في تسجيل الدخول']
            };
          case 422:
            return {
              success: false,
              data: response.data,
              message: response.data.message,
              errorType: 'validation',
              errorMessages: response.data.error_messages || []
            };
          
          case 500:
            return {
              success: false,
              data: response.data,
              message: response.data.message,
              errorType: 'authentication',
              errorMessages: [response.data.message]
            };
          
          default:
            return {
              success: false,
              data: response.data,
              message: response.message,
              errorType: 'general',
              errorMessages: [response.message]
            };
        }
      }
    } catch (error) {
      return {
        success: false,
        data: null,
        message: 'Network error occurred',
        errorType: 'network',
        errorMessages: [error.message]
      };
    }
  }

  // Company login
  async companyLogin(email, password) {
    try {
      const payload = { email, password };

      const response = await httpClient.post('/api/company/login', payload, { suppressAuthRedirect: true });

      if (response.success) {
        // Normalize to same shape as admin: ensure data.user exists
        const normalized = {
          ...response.data,
          data: {
            ...response.data.data,
            user: response.data?.data?.user || response.data?.data?.company || {}
          }
        };

        // Store the auth token (company token is under data.token)
        try {
          httpClient.setAuthToken(normalized.data.token);
        } catch {}

        // Store user data
        try {
          localStorage.setItem('user_data', JSON.stringify(normalized.data.user));
        } catch {}

        return {
          success: true,
          data: normalized,
          message: response.data.message
        };
      } else {
        // Handle different error statuses
        switch (response.status) {
          case 401:
            return {
              success: false,
              data: response.data,
              message: 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
              errorType: 'authentication',
              errorMessages: ['البريد الإلكتروني أو كلمة المرور غير صحيحة']
            };
          case 422:
            return {
              success: false,
              data: response.data,
              message: response.data.message,
              errorType: 'validation',
              errorMessages: response.data.error_messages || []
            };

          case 500:
            return {
              success: false,
              data: response.data,
              message: response.data.message,
              errorType: 'authentication',
              errorMessages: [response.data.message]
            };

          default:
            return {
              success: false,
              data: response.data,
              message: response.message,
              errorType: 'general',
              errorMessages: [response.message]
            };
        }
      }
    } catch (error) {
      return {
        success: false,
        data: null,
        message: 'Network error occurred',
        errorType: 'network',
        errorMessages: [error.message]
      };
    }
  }

  // Logout
  async logout() {
    try {
      // Call logout endpoint if available
      await httpClient.post('/api/admin/logout');
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      // Clear local storage regardless of API call success
      httpClient.setAuthToken(null);
      clearAuthData();
    }
  }

  // Logout without API call (for immediate logout)
  logoutImmediate() {
    httpClient.setAuthToken(null);
    clearAuthData();
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = httpClient.getAuthToken();
    return !!token;
  }

  // Get current user data
  getCurrentUser() {
    try {
      const userData = localStorage.getItem('user_data');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }

  // Check if user has specific permission
  hasPermission(permission) {
    const user = this.getCurrentUser();
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  }

  // Check if user has admin role
  isAdmin() {
    const user = this.getCurrentUser();
    if (!user || !user.roles) return false;
    return user.roles.includes('admin');
  }
}

export default new AuthService();
